<template>
    <div>
        <div class="protocol-chrome">
            <div class="protocol-chrome-top">
                <h2 class="protocol-chrome-title">
                    {{ buildingName }} - Dokument
                </h2>
                <div class="protocol-chrome-actions">
                    <div class="protocol-view-toggle" role="group" aria-label="Visningsläge">
                        <button type="button"
                                class="protocol-view-toggle-btn"
                                :class="{ active: viewMode === 'list' }"
                                title="Lista"
                                @click="setViewMode('list')">
                            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
                                <rect x="1" y="2" width="3" height="3" rx="0.6" />
                                <rect x="6" y="2.5" width="9" height="2" rx="1" />
                                <rect x="1" y="6.5" width="3" height="3" rx="0.6" />
                                <rect x="6" y="7" width="9" height="2" rx="1" />
                                <rect x="1" y="11" width="3" height="3" rx="0.6" />
                                <rect x="6" y="11.5" width="9" height="2" rx="1" />
                            </svg>
                        </button>
                        <button type="button"
                                class="protocol-view-toggle-btn"
                                :class="{ active: viewMode === 'icons' }"
                                title="Ikoner"
                                @click="setViewMode('icons')">
                            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
                                <rect x="1" y="1" width="6" height="6" rx="1.2" />
                                <rect x="9" y="1" width="6" height="6" rx="1.2" />
                                <rect x="1" y="9" width="6" height="6" rx="1.2" />
                                <rect x="9" y="9" width="6" height="6" rx="1.2" />
                            </svg>
                        </button>
                    </div>
                    <b-button v-if="canManage" variant="primary" class="protocol-action-btn" @click="openCreateFolder">
                        <font-awesome-icon icon="folder" class="mr-1" />
                        <span>Ny mapp</span>
                    </b-button>
                    <FileUploader v-if="canManage"
                                  ref="uploader"
                                  class="protocol-upload"
                                  :entity-type="entityType"
                                  :entity-id="buildingId"
                                  :folder-id="folderId"
                                  as-button
                                  hide-existing
                                  @uploaded="getData" />
                </div>
            </div>

            <nav v-if="breadcrumbs.length" class="protocol-path" aria-label="sökväg">
                <template v-for="(crumb, index) in breadcrumbs">
                    <span :key="crumb.id == null ? 'root' : crumb.id" class="protocol-path-item">
                        <a v-if="crumb.id !== folderId"
                           href="#"
                           @click.prevent="openFolder(crumb.id)">
                            {{ crumb.name }}
                        </a>
                        <span v-else class="font-weight-bold">{{ crumb.name }}</span>
                    </span>
                    <font-awesome-icon v-if="index < breadcrumbs.length - 1"
                                       :key="`sep-${index}`"
                                       icon="chevron-right"
                                       class="protocol-path-sep text-muted" />
                </template>
            </nav>

            <div class="protocol-chrome-filter">
                <b-input-group>
                    <b-form-input id="filter-input"
                                  v-model="filter"
                                  type="search"
                                  placeholder="Filtrera på namn...." />
                    <b-input-group-append v-if="filter">
                        <b-button @click="filter = ''">
                            Rensa
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
                <div v-show="totalRows > perPage" class="protocol-page-size">
                    <PaginationPageSizeSelect id="per-page-select" v-model="perPage" />
                </div>
            </div>
        </div>

        <b-pagination v-show="totalRows > perPage"
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      align="center"
                      class="mt-3 mb-0" />

        <FileDropzone :disabled="!canManage"
                      @drop="onFilesDropped">
            <div v-if="isBusy" class="text-center mt-4">
                <font-awesome-icon class="align-middle" icon="spinner" size="2x" spin />
            </div>

            <div v-else-if="!filteredItems.length" class="text-center mt-4">
                <h4>{{ filter ? 'Inga resultat' : 'Inga dokument' }}</h4>
                <h6 v-if="filter">
                    <a href="#" @click.prevent="filter = ''">
                        Klicka här för att rensa aktiv filtrering
                    </a>
                </h6>
                <p v-else-if="canManage" class="text-muted mb-0">
                    Dra och släpp filer här för att ladda upp
                </p>
            </div>

            <div v-else-if="viewMode === 'icons'" class="protocol-icon-grid mt-3">
                <div v-for="item in pagedItems"
                     :key="`${item.kind}-${item.id}`"
                     class="protocol-icon-tile"
                     role="button"
                     @click="onRowClicked(item)">
                    <div v-if="canManage && canShowActions(item)" class="protocol-icon-tile-menu" @click.stop>
                        <b-dropdown variant="link" size="sm" no-caret right toggle-class="protocol-icon-menu-toggle">
                            <template #button-content>
                                <font-awesome-icon icon="ellipsis-v" />
                            </template>
                            <!-- <b-dropdown-item v-if="item.kind === 'folder' && !item.isReserved" @click="openRenameFolder(item)">
                                Byt namn
                            </b-dropdown-item> -->
                            <b-dropdown-item v-if="canMove(item)" @click="openMove(item)">
                                Flytta
                            </b-dropdown-item>
                            <b-dropdown-item v-if="canDelete(item)" @click="confirmRemove(item)">
                                Ta bort
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                    <font-awesome-icon :icon="itemIcon(item)"
                                       size="3x"
                                       :class="itemIconClass(item)" />
                    <div class="protocol-icon-name" :title="item.name">{{ item.name }}</div>
                </div>
            </div>

            <b-table v-else
                     :items="filteredItems"
                     :fields="fields"
                     :current-page="currentPage"
                     :per-page="perPage"
                     :sort-by.sync="sortBy"
                     :sort-desc.sync="sortDesc"
                     :sort-direction="sortDirection"
                     :sort-compare="sortCompare"
                     stacked="sm"
                     small
                     hover
                     class="mt-2"
                     @filtered="onFiltered"
                     @context-changed="onContextChanged"
                     @row-clicked="onRowClicked">
            <template #cell(name)="row">
                <a href="#" @click.prevent="onRowClicked(row.item)">
                    <font-awesome-icon :icon="itemIcon(row.item)"
                                       class="mr-1"
                                       :class="itemIconClass(row.item)" />
                    {{ row.item.name }}
                </a>
            </template>

            <template #cell(kind)="row">
                {{ kindLabel(row.item) }}
            </template>

            <template #cell(created)="row">
                {{ formatDateTime(row.item.created) }}
            </template>

            <template #cell(size)="row">
                {{ row.item.kind === 'file' ? formatFileSize(row.item.fileSize) : '' }}
            </template>

            <template #cell(actions)="row">
                <div v-if="canManage" class="text-nowrap" @click.stop>
                    <!-- <b-button v-if="row.item.kind === 'folder' && !row.item.isReserved"
                              variant="default"
                              size="sm"
                              title="Byt namn"
                              @click="openRenameFolder(row.item)">
                        <font-awesome-icon icon="pen-square" />
                    </b-button> -->
                    <b-button v-if="canMove(row.item)"
                              variant="default"
                              size="sm"
                              title="Flytta"
                              @click="openMove(row.item)">
                        <font-awesome-icon icon="arrows-alt-h" />
                    </b-button>
                    <b-button v-if="canDelete(row.item)"
                              variant="default"
                              size="sm"
                              title="Ta bort"
                              @click="confirmRemove(row.item)">
                        <font-awesome-icon icon="trash-alt" />
                    </b-button>
                </div>
            </template>

            </b-table>
        </FileDropzone>

        <b-modal id="building-protocols-folder-modal"
                 :title="folderForm.id ? 'Byt namn på mapp' : 'Ny mapp'"
                 ok-title="Spara"
                 cancel-title="Avbryt"
                 @ok.prevent="saveFolder">
            <b-form-group label="Namn" label-for="folder-name-input">
                <b-form-input id="folder-name-input"
                              :value="folderForm.name"
                              maxlength="150"
                              @input="onFolderNameInput"
                              @keyup.enter.native="saveFolder" />
                <small class="form-text text-muted">
                    Tecken som inte är tillåtna: \ / : * ? " &lt; &gt; |
                </small>
            </b-form-group>
            <b-alert :show="!!folderError" variant="danger">{{ folderError }}</b-alert>
        </b-modal>

        <b-modal id="building-protocols-move-modal"
                 title="Flytta"
                 @ok.prevent="saveMove"
                 cancel-title="Avbryt">
            <b-form-group label="Till mapp" label-for="move-target-select">
                <b-form-select id="move-target-select"
                               v-model="moveForm.targetFolderId"
                               :options="moveTargetOptions" />
            </b-form-group>
            <b-alert :show="!!moveError" variant="danger">{{ moveError }}</b-alert>
        </b-modal>
    </div>
</template>

<script>
    import { get, post, remove } from '@/helpers/api';
    import PaginationPageSizeSelect from '@/components/PaginationPageSizeSelect';
    import FileUploader from '@/components/file/FileUploader';
    import FileDropzone from '@/components/file/FileDropzone';
    import { ENTITY_TYPES } from '@/helpers/entity-type.helper';

    const VIEW_MODE_KEY = 'building-protocols-view';

    export default {
        name: 'BuildingProtocols',
        components: {
            PaginationPageSizeSelect,
            FileUploader,
            FileDropzone
        },
        props: {
            buildingId: {
                type: Number,
                required: true
            },
            buildingName: {
                type: String,
                required: false,
                default: ''
            },
            canManage: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                folderId: null,
                breadcrumbs: [],
                items: [],
                allFolders: [],
                fields: [
                    { key: 'name', label: 'Namn', sortable: true, sortDirection: 'asc' },
                    { key: 'kind', label: 'Typ', sortable: true, sortDirection: 'asc' },
                    { key: 'created', label: 'Uppladdad', sortable: true, sortDirection: 'desc' },
                    { key: 'size', label: 'Storlek', sortable: true, sortDirection: 'desc', class: 'text-nowrap' },
                    { key: 'actions', label: '', class: 'text-right' }
                ],
                totalRows: 0,
                currentPage: 1,
                perPage: 10,
                sortBy: 'name',
                sortDesc: false,
                sortDirection: 'asc',
                filter: '',
                viewMode: 'list',
                isBusy: true,
                folderForm: { id: 0, name: '' },
                folderError: '',
                moveForm: { item: null, targetFolderId: null },
                moveError: ''
            };
        },
        computed: {
            entityType() {
                return ENTITY_TYPES.buildingProtocol.id;
            },
            canUse() {
                return this.buildingId > 0;
            },
            filteredItems() {
                const query = (this.filter || '').trim().toLowerCase();
                return query
                    ? this.items.filter((item) => (item.name || '').toLowerCase().includes(query))
                    : this.items;
            },
            pagedItems() {
                const items = [...this.filteredItems].sort((a, b) => {
                    const kind = this.sortCompare(a, b, 'name', false);
                    if (kind !== false) {
                        return kind;
                    }

                    return (a.name || '').localeCompare(b.name || '', 'sv', { sensitivity: 'base' });
                });

                const start = (this.currentPage - 1) * this.perPage;
                return items.slice(start, start + this.perPage);
            },
            moveTargetOptions() {
                const current = this.moveForm.item;
                const blocked = new Set();
                if (current && current.kind === 'folder') {
                    blocked.add(current.id);
                    this.collectDescendantIds(current.id, blocked);
                }

                const options = [{ value: null, text: 'Dokument (rot)' }];
                this.allFolders
                    .filter((folder) => !blocked.has(folder.id))
                    .map((folder) => ({
                        value: folder.id,
                        text: this.folderPath(folder)
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text, 'sv', { sensitivity: 'base' }))
                    .forEach((option) => {
                        options.push(option);
                    });
                return options;
            }
        },
        watch: {
            buildingId: {
                async handler(newValue, oldValue) {
                    if (!newValue || newValue <= 0) {
                        this.items = [];
                        this.totalRows = 0;
                        this.isBusy = false;
                        return;
                    }

                    if (oldValue !== newValue) {
                        this.folderId = null;
                        await this.getData();
                    }
                },
                immediate: true
            },
            folderId() {
                this.currentPage = 1;
            },
            filteredItems: {
                handler(items) {
                    this.totalRows = items.length;
                },
                immediate: true
            }
        },
        created() {
            const stored = window.localStorage.getItem(VIEW_MODE_KEY);
            if (stored === 'list' || stored === 'icons') {
                this.viewMode = stored;
                return;
            }

            this.viewMode = window.innerWidth <= 767 ? 'icons' : 'list';
        },
        methods: {
            setViewMode(mode) {
                this.viewMode = mode;
                window.localStorage.setItem(VIEW_MODE_KEY, mode);
            },
            sortCompare(a, b, key, sortDesc) {
                if (a.kind !== b.kind) {
                    const folderFirst = a.kind === 'folder' ? -1 : 1;
                    return sortDesc ? -folderFirst : folderFirst;
                }

                return false;
            },
            onContextChanged(ctx) {
                if (ctx.perPage > this.totalRows) {
                    this.currentPage = 1;
                }
            },
            onFiltered(filteredItems) {
                this.totalRows = filteredItems.length;
            },
            onFilesDropped(files) {
                if (!this.canManage || !files || !files.length || !this.$refs.uploader) {
                    return;
                }

                this.$refs.uploader.uploadFiles(files);
            },
            onRowClicked(item) {
                if (!item) {
                    return;
                }

                if (item.kind === 'folder') {
                    this.openFolder(item.id);
                    return;
                }

                this.openFile(item);
            },
            openFolder(id) {
                this.folderId = id == null ? null : id;
                this.getData();
            },
            openFile(item) {
                if (!item || !item.id) {
                    return;
                }

                window.open(this.getViewUrl(item.id), '_blank', 'noopener');
            },
            getViewUrl(fileId) {
                return `${process.env.VUE_APP_ROOT_API}File/View/${fileId}`;
            },
            itemIcon(item) {
                if (item.kind === 'folder') {
                    return 'folder';
                }

                const ext = this.fileExt(item);
                if (ext === 'pdf') {
                    return 'file-pdf';
                }
                if (ext === 'doc' || ext === 'docx') {
                    return 'file-word';
                }
                if (ext === 'xls' || ext === 'xlsx') {
                    return 'file-excel';
                }
                if (ext === 'ppt' || ext === 'pptx') {
                    return 'file-powerpoint';
                }
                if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'tif', 'tiff'].includes(ext)) {
                    return 'file-image';
                }

                return 'file';
            },
            itemIconClass(item) {
                if (item.kind === 'folder') {
                    return 'protocol-folder-icon';
                }

                const ext = this.fileExt(item);
                if (ext === 'pdf') {
                    return 'protocol-file-pdf';
                }
                if (ext === 'doc' || ext === 'docx') {
                    return 'protocol-file-word';
                }
                if (ext === 'xls' || ext === 'xlsx') {
                    return 'protocol-file-excel';
                }
                if (ext === 'ppt' || ext === 'pptx') {
                    return 'protocol-file-powerpoint';
                }
                if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'tif', 'tiff'].includes(ext)) {
                    return 'protocol-file-image';
                }

                return 'text-secondary';
            },
            fileExt(item) {
                return (item.fileExtension || '').toLowerCase().replace(/^\./, '');
            },
            canShowActions(item) {
                return /* (item.kind === 'folder' && !item.isReserved) || */ this.canMove(item) || this.canDelete(item);
            },
            kindLabel(item) {
                if (item.kind === 'folder') {
                    return item.isReserved ? 'Protokoll' : 'Mapp';
                }
                return item.fileExtension || 'Fil';
            },
            canMove(item) {
                return item.kind === 'file' || (item.kind === 'folder' && !item.isReserved);
            },
            canDelete(item) {
                return item.kind === 'file' || (item.kind === 'folder' && !item.isReserved);
            },
            formatDateTime(value) {
                if (!value) {
                    return '';
                }

                const d = new Date(value);
                if (Number.isNaN(d.getTime())) {
                    return value;
                }

                const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
            },
            formatFileSize(bytes) {
                if (bytes == null || Number.isNaN(Number(bytes))) {
                    return '';
                }

                const size = Number(bytes);
                if (size < 1024) {
                    return `${size} B`;
                }

                const kb = size / 1024;
                if (kb < 1024) {
                    return `${kb.toFixed(1)} KB`;
                }

                const mb = kb / 1024;
                return `${mb.toFixed(1)} MB`;
            },
            folderPath(folder) {
                const names = [];
                let current = folder;
                const guard = new Set();
                while (current && !guard.has(current.id)) {
                    names.unshift(current.name);
                    guard.add(current.id);
                    current = this.allFolders.find((f) => f.id === current.parentId);
                }
                return names.join(' / ');
            },
            collectDescendantIds(folderId, target) {
                this.allFolders
                    .filter((folder) => folder.parentId === folderId)
                    .forEach((folder) => {
                        target.add(folder.id);
                        this.collectDescendantIds(folder.id, target);
                    });
            },
            openCreateFolder() {
                this.folderForm = { id: 0, name: '' };
                this.folderError = '';
                this.$bvModal.show('building-protocols-folder-modal');
            },
            // openRenameFolder(item) {
            //     this.folderForm = { id: item.id, name: item.name };
            //     this.folderError = '';
            //     this.$bvModal.show('building-protocols-folder-modal');
            // },
            sanitizeFolderName(value) {
                return (value || '').replace(/[<>:"/\\|?*\u0000-\u001f]/g, '');
            },
            onFolderNameInput(value) {
                this.folderForm.name = this.sanitizeFolderName(value);
                this.folderError = '';
            },
            hasDuplicateFolderName(name) {
                const normalized = name.toLowerCase();
                return this.items.some((item) =>
                    item.kind === 'folder'
                    && item.id !== this.folderForm.id
                    && (item.name || '').toLowerCase() === normalized);
            },
            async saveFolder() {
                const name = this.sanitizeFolderName(this.folderForm.name || '').trim();
                this.folderForm.name = name;
                if (!name) {
                    this.folderError = 'Ange ett namn.';
                    return;
                }

                if (name === '.' || name === '..' || name.endsWith('.')) {
                    this.folderError = 'Mappnamnet är inte tillåtet i Windows.';
                    return;
                }

                if (this.hasDuplicateFolderName(name)) {
                    this.folderError = 'Det finns redan en mapp med det namnet här.';
                    return;
                }

                this.folderError = '';
                try {
                    await post('File', `SaveFolder/${this.entityType}/${this.buildingId}`, {
                        id: this.folderForm.id,
                        name,
                        parentId: this.folderForm.id ? undefined : this.folderId,
                        entityType: this.entityType,
                        entityId: this.buildingId
                    });
                    this.$bvModal.hide('building-protocols-folder-modal');
                    await this.getData();
                } catch (error) {
                    this.folderError = this.errorMessage(error, 'SaveFolder', 'Det gick inte att spara mappen.');
                }
            },
            openMove(item) {
                this.moveForm = {
                    item,
                    targetFolderId: item.kind === 'folder' ? item.parentId : item.folderId
                };
                this.moveError = '';
                this.$bvModal.show('building-protocols-move-modal');
            },
            async saveMove() {
                const item = this.moveForm.item;
                if (!item) {
                    return;
                }

                this.moveError = '';
                try {
                    if (item.kind === 'folder') {
                        await post('File', `MoveFolder/${item.id}`, {
                            folderId: item.id,
                            targetFolderId: this.moveForm.targetFolderId
                        });
                    } else {
                        await post('File', `Move/${item.id}`, {
                            fileId: item.id,
                            targetFolderId: this.moveForm.targetFolderId
                        });
                    }
                    this.$bvModal.hide('building-protocols-move-modal');
                    await this.getData();
                } catch (error) {
                    this.moveError = this.errorMessage(
                        error,
                        item.kind === 'folder' ? 'MoveFolder' : 'MoveFile',
                        'Det gick inte att flytta.'
                    );
                }
            },
            async confirmRemove(item) {
                const message = item.kind === 'folder'
                    ? 'Mappen och allt innehåll tas bort.'
                    : 'Filen kommer att raderas.';

                const confirmed = await this.$bvModal.msgBoxConfirm(message, {
                    title: 'Är du säker?',
                    size: 'sm',
                    cancelVariant: 'primary',
                    cancelTitle: 'Nej',
                    okTitle: 'Ja',
                    okVariant: 'danger',
                    footerClass: 'p-2 justify-content-between',
                    hideHeaderClose: false,
                    centered: true
                });

                if (!confirmed) {
                    return;
                }

                try {
                    if (item.kind === 'folder') {
                        await remove('File', `RemoveFolder/${item.id}`);
                    } else {
                        await remove('File', `Remove/${item.id}`);
                    }
                    await this.getData();
                } catch (error) {
                    alert(this.errorMessage(error, item.kind === 'folder' ? 'DeleteFolder' : 'DeleteFile', 'Det gick inte att ta bort.'));
                }
            },
            errorMessage(error, code, fallback) {
                const errors = error && error.response && error.response.data && error.response.data.errors;
                if (errors && errors[code] && errors[code][0]) {
                    return errors[code][0];
                }
                return fallback;
            },
            mapItems(data) {
                const folders = (data.folders || []).map((folder) => ({
                    ...folder,
                    kind: 'folder',
                    name: folder.name,
                    created: folder.created,
                    fileSize: 0
                }));

                const files = (data.files || []).map((file) => ({
                    ...file,
                    kind: 'file',
                    name: file.fileName,
                    fileExtension: file.fileExtension || (file.fileName && file.fileName.includes('.')
                        ? `.${file.fileName.split('.').pop()}`
                        : '')
                }));

                return [...folders, ...files];
            },
            async getData() {
                if (!this.canUse) {
                    return;
                }

                this.isBusy = true;
                const folderQuery = this.folderId != null ? `?folderId=${this.folderId}` : '';

                try {
                    const [browse, folders] = await Promise.all([
                        get('File', `Browse/${this.entityType}/${this.buildingId}${folderQuery}`),
                        get('File', `GetFolders/${this.entityType}/${this.buildingId}`)
                    ]);

                    this.breadcrumbs = browse.data.breadcrumbs || [];
                    this.items = this.mapItems(browse.data);
                    this.allFolders = folders.data || [];
                    this.totalRows = this.items.length;
                    this.isBusy = false;
                } catch (error) {
                    this.items = [];
                    this.breadcrumbs = [];
                    this.totalRows = 0;
                    this.isBusy = false;
                }
            }
        }
    };
</script>

<style scoped>
    .protocol-chrome {
        margin-bottom: 0.25rem;
    }

    .protocol-chrome-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
    }

    .protocol-chrome-title {
        margin: 0;
        min-width: 0;
        flex: 1 1 auto;
    }

    .protocol-chrome-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.5rem;
        flex: 0 0 auto;
    }

    .protocol-chrome-filter {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-top: 0.75rem;
    }

    .protocol-chrome-filter .input-group {
        flex: 1 1 auto;
        min-width: 0;
    }

    .protocol-page-size {
        flex: 0 0 auto;
        min-width: 110px;
    }

    .protocol-view-toggle {
        display: inline-flex;
        vertical-align: middle;
        overflow: hidden;
        border: 1px solid #ced4da;
        border-radius: 0.35rem;
        background: #fff;
    }

    .protocol-view-toggle-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 32px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #6c757d;
        line-height: 1;
    }

    .protocol-view-toggle-btn + .protocol-view-toggle-btn {
        border-left: 1px solid #ced4da;
    }

    .protocol-view-toggle-btn svg {
        fill: currentColor;
    }

    .protocol-view-toggle-btn.active {
        background: #eef2f6;
        color: #212529;
    }

    .protocol-view-toggle-btn:hover:not(.active) {
        background: #f8f9fa;
        color: #495057;
    }

    .protocol-path {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 0.5rem;
        font-size: 0.95rem;
        color: #6c757d;
    }

    .protocol-path-sep {
        margin: 0 0.4rem;
        font-size: 0.7rem;
    }

    .protocol-path-item a {
        color: #007bff;
    }

    .protocol-folder-icon {
        color: #e8a317;
    }

    .protocol-file-pdf {
        color: #c0392b;
    }

    .protocol-file-word {
        color: #2b579a;
    }

    .protocol-file-excel {
        color: #217346;
    }

    .protocol-file-powerpoint {
        color: #d24726;
    }

    .protocol-file-image {
        color: #17a2b8;
    }

    .protocol-icon-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
        gap: 0.75rem;
    }

    .protocol-icon-tile {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        min-height: 124px;
        padding: 1.5rem 0.5rem 0.75rem;
        border: 1px solid #e9ecef;
        border-radius: 0.35rem;
        background: #fff;
        cursor: pointer;
    }

    .protocol-icon-tile:hover,
    .protocol-icon-tile:focus {
        background: #f8f9fa;
        border-color: #ced4da;
    }

    .protocol-icon-name {
        margin-top: 0.6rem;
        width: 100%;
        font-size: 0.85rem;
        line-height: 1.2;
        text-align: center;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        word-break: break-word;
    }

    .protocol-icon-tile-menu {
        position: absolute;
        top: 0;
        right: 0;
    }

    .protocol-icon-menu-toggle {
        padding: 0.15rem 0.4rem;
        color: #6c757d;
    }

    @media (max-width: 767.98px) {
        .protocol-chrome-top {
            flex-direction: column;
            align-items: stretch;
            gap: 0.65rem;
        }

        .protocol-chrome-title {
            font-size: 1.2rem;
            line-height: 1.3;
        }

        .protocol-chrome-actions {
            justify-content: flex-start;
        }

        .protocol-action-btn,
        .protocol-upload {
            flex: 1 1 0;
            min-width: 0;
        }

        .protocol-upload ::v-deep .btn {
            width: 100%;
        }

        .protocol-view-toggle {
            flex: 0 0 auto;
        }

        .protocol-path {
            flex-wrap: nowrap;
            overflow-x: auto;
            margin-top: 0.65rem;
            padding-bottom: 0.15rem;
            white-space: nowrap;
            -webkit-overflow-scrolling: touch;
        }

        .protocol-path-item,
        .protocol-path-sep {
            flex: 0 0 auto;
        }

        .protocol-chrome-filter {
            flex-direction: column;
            align-items: stretch;
            margin-top: 0.65rem;
        }

        .protocol-page-size {
            display: none;
        }
    }

    @media (max-width: 575.98px) {
        .protocol-icon-grid {
            grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
            gap: 0.5rem;
        }

        .protocol-icon-tile {
            min-height: 112px;
            padding: 1.35rem 0.35rem 0.6rem;
        }
    }
</style>
